import { useState, useEffect, useCallback } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const THEME_KEY = 'perch-theme-preference';

/**
 * Three-way theme toggle (light / dark / system) with OS preference tracking.
 *
 * "system" mode listens to the prefers-color-scheme media query and
 * re-applies whenever the OS switches. The meta theme-color tag is also
 * updated so mobile browser chrome matches the app background (stone-50
 * for light, stone-950 for dark).
 */
export function useTheme() {
  // Initialize from localStorage or default to 'system'
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return 'system';
  });

  // Track OS dark-mode preference as state so resolvedTheme is derived
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Derived — no setState needed
  const resolvedTheme: ResolvedTheme =
    (theme === 'dark' || (theme === 'system' && systemDark)) ? 'dark' : 'light';

  // Apply resolved theme to DOM
  const applyToDOM = useCallback((isDark: boolean) => {
    document.documentElement.classList.toggle('dark', isDark);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', isDark ? '#0C0A09' : '#FAFAF9');
    }
  }, []);

  // Sync DOM whenever resolvedTheme changes
  useEffect(() => {
    applyToDOM(resolvedTheme === 'dark');
  }, [resolvedTheme, applyToDOM]);

  // Listen for OS preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Set theme with persistence
  const setTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(THEME_KEY, newTheme);
    setThemeState(newTheme);
  }, []);

  return {
    theme,           // 'light' | 'dark' | 'system'
    resolvedTheme,   // 'light' | 'dark' (actual applied theme)
    setTheme,
    isDark: resolvedTheme === 'dark',
  };
}
